import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { DataLabel } from "./common";
import { CheckCircle, XCircle, ClockCountdown, ArrowsClockwise, Warning, CircleNotch } from "@phosphor-icons/react";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export function FailedTodayBanner() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/agents/failed-today").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const retry = async () => {
    if (!window.confirm(`Relancer uniquement les ${items.length} étape(s) en échec aujourd'hui ?\n\n${items.map((i) => "• " + i.label).join("\n")}`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/agents/retry-failed");
      toast.success(data.started ? `${data.count} étape(s) relancée(s)` : "Aucune étape en échec");
      setTimeout(load, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Relance impossible"); }
    finally { setBusy(false); }
  };

  if (!items.length) return null;
  return (
    <div data-testid="failed-today-banner" className="mb-4 border border-red-200 bg-red-50 rounded-md p-4 flex flex-wrap items-start gap-4">
      <Warning size={22} weight="fill" className="text-red-600 mt-0.5" />
      <div className="flex-1 min-w-[240px]">
        <div className="text-sm font-medium text-red-800">{items.length} étape(s) en échec aujourd'hui</div>
        <ul className="mt-1 space-y-0.5">
          {items.map((i) => (
            <li key={i.run_id} data-testid={`failed-item-${i.run_id}`} className="text-xs text-red-700 truncate">
              {i.label} <span className="text-red-500/80 font-mono">— {fmt(i.started_at)}</span>
            </li>
          ))}
        </ul>
      </div>
      <button data-testid="retry-failed-btn" onClick={retry} disabled={busy}
        className="flex items-center gap-2 bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 px-4 py-2 rounded-md text-sm font-medium transition-colors">
        {busy ? <CircleNotch size={16} className="ai-pulse" /> : <ArrowsClockwise size={16} />} Relancer les échecs
      </button>
    </div>
  );
}

function StatusIcon({ run }) {
  if (!run) return <ClockCountdown size={15} className="text-muted-foreground" />;
  return run.status === "error"
    ? <XCircle size={15} weight="fill" className="text-red-500" />
    : <CheckCircle size={15} weight="fill" className="text-emerald-600" />;
}

export function SchedulerJobs() {
  const [jobs, setJobs] = useState([]);
  useEffect(() => {
    const load = () => api.get("/scheduler/jobs").then((r) => setJobs(r.data || [])).catch(() => {});
    load(); const t = setInterval(load, 30000); return () => clearInterval(t);
  }, []);

  return (
    <div className="mt-6" data-testid="scheduler-jobs">
      <DataLabel>Tâches automatiques — dernière & prochaine exécution</DataLabel>
      <div className="mt-3 bg-white border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] font-mono uppercase tracking-wide text-muted-foreground border-b border-border">
              <th className="text-left px-4 py-2.5">Tâche</th>
              <th className="text-left px-4 py-2.5">Dernière exécution</th>
              <th className="text-left px-4 py-2.5">Prochaine exécution</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {jobs.length === 0 && <tr><td colSpan={3} className="px-4 py-4 text-muted-foreground">Planificateur indisponible.</td></tr>}
            {jobs.map((j) => (
              <tr key={j.id} data-testid={`job-row-${j.id}`}>
                <td className="px-4 py-2.5 font-medium">{j.label}</td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1.5">
                    <StatusIcon run={j.last_run} />
                    <span className="font-mono text-xs">{j.last_run ? fmt(j.last_run.started_at) : "Pas encore exécutée"}</span>
                  </div>
                  {j.last_run?.status === "error" && j.last_run.error && (
                    <div className="text-[11px] text-red-600 truncate max-w-[320px]" title={j.last_run.error}>{j.last_run.error}</div>
                  )}
                </td>
                <td className="px-4 py-2.5 font-mono text-xs" data-testid={`job-next-${j.id}`}>{fmt(j.next_run)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
